import {
  AppToSitesMessage,
  SitesToAppMessage,
} from '@practicegenius/domain/src/sites-mobile-app/messages';
import {isClient} from './environment';

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: typeof postMessage;
    };
  }
}

const USER_AGENT_KEY = 'RewardsHubMobile';
const userAgentVersionPattern = `^${USER_AGENT_KEY}\\/(?<version>[\\d|\\.]{5,}).*`;

const mobileAppVersion = isClient
  ? window.navigator.userAgent.match(new RegExp(userAgentVersionPattern))
      ?.groups?.version
  : null;

export const inMobileApp = Boolean(mobileAppVersion);

/** If we're in the mobile app, send a post message to it */
export const postMessageToMobileApp = (payload: SitesToAppMessage) => {
  if (!inMobileApp) return;

  // If we think we're in the mobile app but don't have `ReactNativeWeView`, then
  //   we're probably in a testing environment of some sort.
  // In this case, we want to mock the `ReactNativeWebView` object so the UI will
  //   work as it does in the mobile app. This also gives us the ability to inspect
  //   the messages sent to the mobile app.
  if (!window.ReactNativeWebView) {
    const mockRnWebView = {
      messages: [] as unknown[],
      postMessage: (message: unknown) => mockRnWebView.messages.push(message),
    };
    window.ReactNativeWebView = mockRnWebView;
  }

  return window.ReactNativeWebView.postMessage(JSON.stringify(payload));
};

/** Initial processing steps for message payload from the mobile app */
export const parseAppToSitesMessage = (
  payload: any
): AppToSitesMessage | null => {
  try {
    return JSON.parse(payload);
  } catch (e) {
    return null;
  }
};

/** Given a URL, generate a click handler that opens the link in an external browser
 *    when rendered in the mobile app (and works normally elsewhere) */
export const onClickExternalLink =
  (url: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!inMobileApp) return;

    e.preventDefault();
    e.stopPropagation();

    postMessageToMobileApp({
      type: 'request_open_external_url',
      payload: {
        url,
      },
    });
  };
